import PropTypes from 'prop-types';
import { useRef, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { Card, CardContent, Box, Typography, Switch } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import QuillEditor from 'src/components/quill-editor';
import SubmitButton, { useCustomFormik } from 'src/components/formik-helper';
import AvatarUploader from 'src/components/widgets/avatar-uploader.js';

import StringTextField from 'src/components/widgets/string-textfield';
import SearchSelectField from 'src/components/widgets/search-select-field';
import { GenderOptions } from 'src/constants/select-options';
import DropdownPartners from 'src/components/dropdown/dropdown-partners';
import DropdownDegree from 'src/components/dropdown/dropdown-degree';
import DropdownGroups from 'src/components/dropdown/dropdown-groups';
import { mergeNoteFromEditor } from 'src/utils/formatters';
import { useTranslation } from 'react-i18next';
import { appStr } from 'src/locales/app_strings';
import { formatInputByPattern } from 'src/utils/formatters';
import {
  initialValues,
  validationSchema,
  handleSubmitted,
} from './formik';

export const DetailForm = forwardRef(({ item, apiInsert, apiUpdate, apiRemove }, ref) => {
  const { t } = useTranslation();
  const editorRef = useRef();
  const genderRef = useRef();
  const partnerRef = useRef();
  const degreeRef = useRef();
  const avatarRef = useRef();
  const groupRef = useRef();

  const [element, setElement] = useState({
    avatar: item?.avatar ?? null,
    gender: item?.gender ?? null,
  });

  const formik = useCustomFormik({
    initialValues: {
      nameField: null, phoneField: null, 
      degreeField: null, partnerField: null,
    },
    validationSchema: validationSchema(element.id),
    onSubmit: async (values, helpers) => {
      const mergeValues = mergeNoteFromEditor(values, editorRef, element.note);
      console.log('=====', mergeValues);
      await handleSubmitted(element.id, mergeValues, apiInsert, apiUpdate, apiRemove);
    },
  });

  useEffect(() => {
    const json = initialValues(item);
    setElement(json);
  }, []);
  
  useImperativeHandle(ref, () => ({
    setDataFromParent: (value) => {
      console.log('CREATED ===', value);

      // Reset dropdown
      editorRef.current?.reset?.();
      genderRef.current?.reset?.();
      partnerRef.current?.reset?.();
      degreeRef.current?.reset?.();
      avatarRef.current?.reset?.();
      groupRef.current?.reset?.();
      
      // Also reset Formik if needed
      formik.resetForm();
    },
  }));

  return (
    <form onSubmit={formik.handleSubmit}>
    <Card>
      <CardContent sx={{ px: 1, py: 2 }}>
        <Grid container spacing={3}>
          <Grid xs={12} md={9}>
            <><Grid container spacing={3}>
              <Grid xs={12} md={6}>
                <StringTextField label="Họ tên" name="nameField" 
                  formik={formik} hintValue={element.name} />
              </Grid>

              <Grid xs={12} md={3}>
                <StringTextField label="Số điện thoại" name="phoneField" inputProps={{ maxLength: 12 }}
                  formik={formik} hintValue={element.phone}
                  onChangeValue={(val) => {
                    const formatted = formatInputByPattern(val, [4, 3, 3], ' ');
                    return formatted;
                  }} />
              </Grid>

              <Grid xs={12} md={3}>
                <StringTextField label="Sinh nhật (d/m/y)" name="birthdayField" inputProps={{ maxLength: 10 }}
                  formik={formik} hintValue={element.birthday}
                  onChangeValue={(val) => {
                    const formatted = formatInputByPattern(val, [2, 2, 4], '/');
                    return formatted;
                  }} />
              </Grid>

              <Grid xs={12} md={6}>
                <StringTextField label="Email" name="emailField" 
                  formik={formik} hintValue={element.email} />
              </Grid>
              
              <Grid xs={12} md={3}>
                <StringTextField label="Căn cước" name="idCardField" 
                  formik={formik} hintValue={element.identityCard}
                  onChangeValue={(val) => {
                    const formatted = formatInputByPattern(val, [4, 4, 5], ' ');
                    return formatted;
                  }} />
              </Grid>

              <Grid xs={12} md={3}>
                <SearchSelectField
                  ref={genderRef}
                  label="Giới tính"
                  name="genderField"
                  options={GenderOptions}
                  onSelected={(obj) => {
                    formik.setFieldValue('genderId', obj?.value);
                  }}
                  hintValue={element.genderLabel}
                />
              </Grid>

              <Grid xs={12} md={12}>
                <StringTextField label="Địa chỉ" name="addressField"
                  formik={formik} hintValue={element.address} />
              </Grid>

            </Grid>
            </>
          </Grid>

          <Grid xs={12} md={3} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <AvatarUploader
              ref={avatarRef}
              avatar={element.avatar}
              gender={element.gender}
              onBlobChange={async (blob, base64) => {
                formik.setFieldValue('avatarField', blob);
              }}
            />

          </Grid>
          
          <Grid xs={12} md={6}>
            <QuillEditor
              ref={editorRef}
              value={element.note}
              sx={{ height: 160 }}
            />
          </Grid>
          
          <Grid xs={12} md={6}>
            <><Grid container spacing={3}>
              <Grid xs={12} md={6}>
                <DropdownDegree
                  ref={degreeRef}
                  label={t(appStr.txt_073)}
                  name="degreeField"
                  type="2" 
                  formik={formik}
                  onSelected={(obj) => {
                    formik.setFieldValue('degreeId', obj?.value)
                  }}
                  hintValue={element.degreeName}
                />
              </Grid>
              <Grid xs={12} md={6}>
                <DropdownPartners
                  ref={partnerRef}
                  label="Hồ sơ"
                  name="partnerField"
                  type="hv" 
                  formik={formik}
                  onSelected={(obj) => {
                    formik.setFieldValue('partnerId', obj?.value)
                  }}
                  hintValue={element.partnerName}
                />
              </Grid>
              <Grid xs={12} md={12}>
                <StringTextField label="Liên hệ" name="contactField" 
                  formik={formik} hintValue={element.partnerContact} />
              </Grid>

              <Grid xs={12} md={12}>
                <DropdownGroups
                  ref={groupRef}
                  name="groupField"
                  type="dat"
                  label="Khu vực"
                  onSelected={(obj) => {
                    formik.setFieldValue('groupId', obj?.value);
                  }}
                  hintValue={element.groupName || ''}
                />
              </Grid>

            </Grid>
            </>
          </Grid>

          <Grid xs={12} md={11} display="flex" 
            sx={{ justifyContent: { xs: 'center', md: 'flex-end' } }}
          >
            <SubmitButton formik={formik} labelSubmit={element.id > 0 ? 'Cập nhật' : 'Tạo mới'} />
          </Grid>
                
        </Grid>
      </CardContent>
    </Card>
    </form>
  );
});

DetailForm.propTypes = {
  apiInsert: PropTypes.func,
  apiUpdate: PropTypes.func,
  apiAssign: PropTypes.func,
};